import apiClient from './api';
import { PresignedUrlResponse, UploadStatusResponse } from '@/types/posts';
import { detectFileTypeByMagicBytes } from '@/utils/magicBytes';

/**
 * Media Service
 *
 * API calls for media upload and management
 */

/**
 * Non-blocking storage-quota nudge, present on upload responses when
 * STORAGE_ENFORCEMENT=warn (backend) and the team is over its storage limit.
 * The upload itself was NOT blocked when this is present — see
 * backend/src/services/storage-quota.service.ts.
 */
export interface StorageWarning {
  usedBytes: number;
  /** null when the team's plan is unlimited storage. */
  limitBytes: number | null;
}

export interface MediaUpload {
  id: string;
  fileName: string;
  originalUrl: string;
  optimizedUrl: string;
  /** null while async video pipeline is still running. */
  thumbnailUrl?: string | null;
  mediaType: 'IMAGE' | 'VIDEO' | 'GIF' | 'PDF';
  fileSize: number;
  width?: number;
  height?: number;
  duration?: number;
  /** Uppercase Prisma enum value (e.g. PENDING, PROCESSING, READY, FAILED). */
  status: 'PENDING' | 'UPLOADING' | 'COMPLETED' | 'PROCESSING' | 'READY' | 'FAILED';
  createdAt: string;
  /** Present (warn mode only) when the team is over its storage limit. */
  storageWarning?: StorageWarning;
}

/**
 * Response shape of GET /api/media/:uploadId/status (media-enhanced.service.ts
 * getUploadStatus). Deliberately a SEPARATE type from `MediaUpload` above —
 * that interface matches the direct-upload / presigned-flow response
 * (`fileName`, `originalUrl`, `optimizedUrl`, `mediaType`), while this
 * endpoint returns the raw `MediaUpload` row fields instead
 * (`filename`, `cloudFrontUrl`, no `mediaType`). Reusing `MediaUpload` here
 * previously type-checked but did not match the real JSON at runtime.
 */
export interface MediaUploadStatusRecord {
  id: string;
  filename: string;
  contentType: string;
  fileSize: number;
  status: 'PENDING' | 'UPLOADING' | 'COMPLETED' | 'PROCESSING' | 'READY' | 'FAILED' | 'CANCELLED';
  uploadProgress: number;
  cloudFrontUrl: string | null;
  errorMessage: string | null;
  createdAt: string;
  compressionRatio?: number | null;
  thumbnailUrl?: string | null;
  metadata?: Record<string, unknown> | null;
}

export interface PostMedia {
  id: number;
  postId: number;
  uploadId: string;
  upload: MediaUpload;
  order: number;
  createdAt: string;
}

/** Status values as returned by the backend (Prisma MediaUploadStatus enum, uppercase). */
export type VideoProcessingStatusValue =
  | 'PENDING'
  | 'UPLOADING'
  | 'COMPLETED'
  | 'PROCESSING'
  | 'READY'
  | 'FAILED';

export interface VideoProcessingStatus {
  uploadId: string;
  /** Uppercase enum value from the backend MediaUploadStatus Prisma enum. */
  status: VideoProcessingStatusValue;
  progress: number; // 0-100
  currentStep?: string;
  error?: string;
  estimatedTimeRemaining?: number; // seconds
  outputFormats?: {
    format: string;
    resolution: string;
    url?: string;
    status: VideoProcessingStatusValue;
  }[];
}

export interface PlatformRequirements {
  platform: string;
  displayName: string;
  image: {
    maxSize: number; // bytes
    maxWidth: number;
    maxHeight: number;
    minWidth: number;
    minHeight: number;
    allowedFormats: string[];
    aspectRatios?: { min: number; max: number };
    maxCount: number;
  };
  video: {
    maxSize: number; // bytes
    maxDuration: number; // seconds
    minDuration?: number;
    maxWidth: number;
    maxHeight: number;
    allowedFormats: string[];
    aspectRatios?: { min: number; max: number };
    maxCount: number;
  };
  gif?: {
    maxSize: number;
    allowedFormats: string[];
  };
}

export interface DirectUploadOptions {
  /**
   * Skips the backend team storage-quota check for this upload. MUST only be
   * set for uploads that are never attached to a post (and therefore never
   * counted toward the team's storage usage), e.g. profile avatars and
   * admin branding logos. Defaults to false (storage check enabled) so
   * content-media uploads (MediaUploadZone via useMediaUpload) remain
   * checked by default.
   */
  skipStorageCheck?: boolean;
}

export const mediaService = {
  /**
   * Upload a file directly to S3 (NEW - replaces tus.js)
   */
  directUpload: async (file: File, options?: DirectUploadOptions): Promise<MediaUpload> => {
    // Send the DETECTED content type, not the raw browser-reported
    // `file.type`. Browsers/OSes report inconsistent (and sometimes generic,
    // e.g. `application/octet-stream`) MIME types for legitimate MP4/MOV/M4V
    // files, and the backend's multer `fileFilter` allowlist checks the
    // multipart part's Content-Type header (which mirrors `file.type` for a
    // plain `File`). Rebuilding the File with the magic-byte-detected type
    // before appending ensures a genuine video is never rejected purely
    // because of a mislabeled/generic browser MIME type. Falls back to the
    // original file untouched when content can't be identified (e.g.
    // non-media uploads, or files that will be rejected downstream anyway).
    const detectedType = await detectFileTypeByMagicBytes(file);
    const uploadFile =
      detectedType && detectedType !== file.type
        ? new File([file], file.name, { type: detectedType, lastModified: file.lastModified })
        : file;

    const formData = new FormData();
    formData.append('file', uploadFile);
    if (options?.skipStorageCheck) {
      formData.append('skipStorageCheck', 'true');
    }

    const response = await apiClient.post('/api/media/direct-upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 300000, // 5 minutes for file uploads (videos can be large)
    });

    return response.data.data.upload;
  },

  /**
   * Attach uploaded media to a post
   */
  attachMediaToPost: async (
    postId: number,
    uploadId: string,
    order?: number,
    altText?: string
  ): Promise<PostMedia> => {
    const response = await apiClient.post(`/api/media/post/${postId}/attach`, {
      uploadId,
      order,
      altText,
    });

    return response.data.data;
  },

  /**
   * Get media for a post
   */
  getPostMedia: async (postId: number): Promise<PostMedia[]> => {
    const response = await apiClient.get(`/api/media/post/${postId}`);
    return response.data.data;
  },

  /**
   * Delete media from a post
   */
  deletePostMedia: async (postMediaId: number): Promise<void> => {
    await apiClient.delete(`/api/media/post-media/${postMediaId}`);
  },

  /**
   * Get upload status
   */
  getUploadStatus: async (uploadId: string): Promise<MediaUploadStatusRecord> => {
    const response = await apiClient.get(`/api/media/${uploadId}/status`);
    return response.data.data;
  },

  /**
   * Delete media upload
   */
  deleteUpload: async (uploadId: string): Promise<void> => {
    await apiClient.delete(`/api/media/${uploadId}`);
  },

  // ============================================================================
  // VIDEO PROCESSING ENDPOINTS (NEW)
  // ============================================================================

  /**
   * Get video processing status
   * Used to poll for video transcoding progress
   */
  getProcessingStatus: async (uploadId: string): Promise<VideoProcessingStatus> => {
    const response = await apiClient.get(`/api/media/upload/${uploadId}/processing-status`);
    return response.data.data;
  },

  /**
   * Retry failed video processing
   */
  retryProcessing: async (uploadId: string): Promise<MediaUpload> => {
    const response = await apiClient.post(`/api/media/upload/${uploadId}/retry-processing`);
    return response.data.data;
  },

  // ============================================================================
  // PLATFORM REQUIREMENTS ENDPOINTS (NEW)
  // ============================================================================

  /**
   * Get media requirements for all platforms
   */
  getAllPlatformRequirements: async (): Promise<PlatformRequirements[]> => {
    const response = await apiClient.get('/api/media/platform-requirements');
    return response.data.data;
  },

  /**
   * Get media requirements for a specific platform
   */
  getPlatformRequirements: async (platform: string): Promise<PlatformRequirements> => {
    const response = await apiClient.get(`/api/media/platform-requirements/${platform}`);
    return response.data.data;
  },

  // ============================================================================
  // LEGACY METHODS (for backward compatibility with tus.js)
  // ============================================================================

  /**
   * Get presigned URL for S3 upload (LEGACY - for tus.js)
   */
  getPresignedUrl: async (
    filename: string,
    contentType: string,
    fileSize: number
  ): Promise<PresignedUrlResponse> => {
    const response = await apiClient.post('/api/media/presigned-url', {
      filename,
      contentType,
      fileSize,
    });
    return response.data.data;
  },

  /**
   * Upload file directly to S3 using a pre-signed PUT URL.
   *
   * Used for browser -> S3 direct upload (currently: videos — see
   * useMediaUpload.uploadFile). Deliberately implemented with a bare
   * XMLHttpRequest rather than the shared `apiClient` axios instance:
   * - `apiClient` would attach the app's `Authorization` header and other
   *   interceptor behavior to a request going to S3, which is signed only
   *   for the exact headers it was presigned with.
   * - `apiClient` has no per-call timeout override baked in here, but more
   *   importantly this call must NOT inherit the 300s ceiling used for
   *   `directUpload` (backend re-upload) — large videos can legitimately
   *   take longer than 5 minutes on a slow connection, and browser->S3 has
   *   no server-side request timeout to race against. `xhr.timeout = 0`
   *   means "no timeout".
   * - XHR (not `fetch`) is used specifically because it exposes real
   *   `upload.onprogress` events, so callers can show genuine upload
   *   percentage instead of a fake fixed value.
   *
   * NOTE: `file.type` here matches the `contentType` passed to
   * `getPresignedUrl`/`initiateUpload` so the object stored in S3 gets the
   * correct `Content-Type` metadata. `@aws-sdk/s3-request-presigner` treats
   * `content-type` as an unsignable header (only `content-length;host` end
   * up in `X-Amz-SignedHeaders`), so S3 does NOT validate this header
   * against the signature — a mismatch will NOT cause a 403
   * SignatureDoesNotMatch.
   */
  uploadToS3: async (
    presignedUrl: string,
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<void> => {
    await new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', presignedUrl, true);
      xhr.setRequestHeader('Content-Type', file.type);
      // No timeout — large video uploads on slow connections must not be
      // capped the way the server-side directUpload path is (300s).
      xhr.timeout = 0;

      xhr.upload.onprogress = (event) => {
        if (onProgress && event.lengthComputable) {
          onProgress(Math.round((event.loaded / event.total) * 100));
        }
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          onProgress?.(100);
          resolve();
        } else {
          reject(new Error(`S3 upload failed with status ${xhr.status}`));
        }
      };

      xhr.onerror = () => reject(new Error('S3 upload failed: network error'));
      xhr.onabort = () => reject(new Error('S3 upload aborted'));

      xhr.send(file);
    });
  },

  /**
   * Complete upload and notify backend (LEGACY - for tus.js)
   */
  completeUpload: async (uploadId: string): Promise<UploadStatusResponse> => {
    const response = await apiClient.post(`/api/media/upload/${uploadId}/complete`);
    return response.data.data;
  },

  /**
   * Cancel upload (LEGACY - for tus.js)
   */
  cancelUpload: async (uploadId: string): Promise<void> => {
    await apiClient.delete(`/api/media/upload/${uploadId}`);
  },
};
